import { Anchor } from "../../app/components/anchor"
import { useRouter } from "next/router"
import Image from "next/image"
import PrimaryButton from "../../components/buttons/PrimaryButton"
import CheckList from "../../components/content/CheckList"
import stateCityDictionary, {
  formatName,
  urlifyName,
} from "../../constants/stateCityDictionary"
import { BreedHalfStarRating } from "../../components/BreedStarRating"
import { isValidDriverId } from "@/util/validateDriver"

interface ITopTransportersProps {
  topTransporters: {
    dba: string
    photo: string
    pickupStateCode: string
    pickupCity: string
    avgRating: string
    userId?: number
  }[]
}

export default function TopTransporters({
  topTransporters,
}: ITopTransportersProps) {
  const { query } = useRouter()
  const { city, state } = query

  const formattedState = formatName(state as string)
  const urlifiedCity = city ? urlifyName(city as string) : ""

  const originalCity =
    stateCityDictionary[formattedState]?.cities.find(
      (dictCity) => urlifyName(dictCity) === urlifiedCity,
    ) ?? ""

  const formattedCity = originalCity ? formatName(originalCity) : ""
  const stateAbbrev = stateCityDictionary[formattedState]?.abbreviation ?? ""

  const locationName = formattedCity
    ? `${formattedCity}, ${stateAbbrev}`
    : formattedState

  return (
    <section className="my-16">
      <h3 className="max-w-[40ch] mx-auto mb-16 font-montserrat font-medium text-3xl text-center text-blue7">
        We are committed to building a safe, caring environment for people and
        animals on the road
      </h3>
      <section className="grid grid-cols-1 md:grid-cols-2 gap-20">
        <aside className="bg-white drop-shadow-md rounded-lg">
          <h4 className="p-4 bg-blue16 flex items-center">
            {/* <AiFillThunderbolt className="h-8 w-auto mr-1.5 fill-yellow-500" /> */}
            Best Pet Shipping Companies in {locationName}
          </h4>
          {topTransporters?.map(({ photo, dba, avgRating, userId }) => {
            const rating = Number(Number(avgRating).toFixed(1))
            return (
              <div
                className="p-6 border-b last-of-type:border-none flex"
                key={dba}
              >
                <Image
                  src={photo}
                  alt={`${locationName} reviews`}
                  width={56}
                  height={56}
                  loading="lazy"
                  className="h-14 w-14 mr-4 rounded-full overflow-hidden object-cover"
                />

                <div>
                  <p className="mb-0 text-lg">{dba}</p>
                  <div className="flex items-center text-sm">
                    <BreedHalfStarRating value={rating} />
                    {rating}/5
                  </div>
                </div>
              </div>
            )
          })}
        </aside>
        <div className="flex flex-col items-center justify-center">
          <CheckList
            className="w-full"
            items={[
              "We carefully background check all drivers.",
              "We verify transporter addresses and contact information for additional protection.",
              "Photos of drivers and their vehicles help confirm who and what to expect.",
              "Detailed customer reviews provide insight and transparency to your selection process.",
            ]}
          />

          <Anchor href="/shipments/new/animals" passHref>
            <PrimaryButton>Get Pet Transportation Quotes</PrimaryButton>
          </Anchor>
        </div>
      </section>
    </section>
  )
}
